import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_KEY;

export const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

// ── visitor / session identity ────────────────────────────────────────────────
function uuid() {
  try { if (crypto && crypto.randomUUID) return crypto.randomUUID(); } catch {}
  return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.floor(Math.random() * 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// Persistent per-browser id → counts unique visitors across sessions.
function getVisitorId() {
  try {
    let id = localStorage.getItem('r88_vid');
    if (!id) { id = uuid(); localStorage.setItem('r88_vid', id); }
    return id;
  } catch { return null; }
}

// Per-tab id → powers "active now" (distinct sessions in the last few minutes).
function getSessionId() {
  try {
    let id = sessionStorage.getItem('r88_sid');
    if (!id) { id = uuid(); sessionStorage.setItem('r88_sid', id); }
    return id;
  } catch { return null; }
}

// AI answer-engines (LLM chat / search) — grouped so we can measure the channel
// that converts far better than the rest. isAiSource() is exported for the admin
// dashboard to roll these up into one "AI" bucket.
const AI_SOURCES = new Set(['chatgpt', 'perplexity', 'gemini', 'claude', 'copilot', 'grok', 'deepseek', 'meta-ai']);
export function isAiSource(src) { return AI_SOURCES.has((src || '').toLowerCase()); }

// Map a raw token (a UTM value like "chatgpt.com", or a referrer hostname) to a
// single canonical bucket — so "utm_source=chatgpt.com" and a chat.openai.com
// referral both land in "chatgpt" instead of fragmenting the numbers.
function canonSource(raw) {
  const s = (raw || '').toLowerCase();
  if (!s) return '';
  if (/chatgpt|openai/.test(s)) return 'chatgpt';
  if (/perplexity/.test(s)) return 'perplexity';
  if (/gemini|bard/.test(s)) return 'gemini';
  if (/claude|anthropic/.test(s)) return 'claude';
  if (/copilot/.test(s)) return 'copilot';       // Microsoft Copilot / Bing chat
  if (/\bgrok\b|x\.ai/.test(s)) return 'grok';
  if (/deepseek/.test(s)) return 'deepseek';
  if (/meta\.ai|meta-ai/.test(s)) return 'meta-ai';
  if (/google\./.test(s) || s === 'google') return 'google';
  if (/bing\./.test(s) || s === 'bing') return 'bing';
  if (/duckduckgo|\bddg\b/.test(s)) return 'duckduckgo';
  if (/yahoo/.test(s)) return 'yahoo';
  if (/reddit/.test(s)) return 'reddit';
  if (/tiktok/.test(s)) return 'tiktok';
  if (/youtube|youtu\.be/.test(s)) return 'youtube';
  if (/instagram/.test(s)) return 'instagram';
  if (/facebook|fb\./.test(s)) return 'facebook';
  if (/linkedin|lnkd\.in/.test(s)) return 'linkedin';
  if (/twitter|t\.co|x\.com/.test(s)) return 'twitter';
  if (/producthunt/.test(s)) return 'producthunt';
  if (/ycombinator|hacker\s*news|hackernews/.test(s)) return 'hackernews';
  return '';
}

// First-touch acquisition source. A UTM tag wins (put ?utm_source=tiktok — or
// reddit / producthunt / youtube … — on every link you post); otherwise we
// derive it from the referrer; otherwise "direct". Stored once per browser so
// the ORIGINAL channel that acquired the visitor sticks (first-touch attribution).
export function getSource() {
  try {
    const stored = localStorage.getItem('r88_src');
    if (stored) return stored;
    let raw = '';
    try {
      const q = new URLSearchParams(window.location.search);
      raw = (q.get('utm_source') || q.get('ref') || '').trim().toLowerCase().slice(0, 60);
    } catch {}
    // A UTM/ref tag wins: normalize it to a canonical bucket, else keep it as-is.
    let src = raw ? (canonSource(raw) || raw.slice(0, 40)) : '';
    if (!src) {
      let host = '';
      try { host = new URL(document.referrer || '').hostname.replace(/^www\./, '').toLowerCase(); } catch {}
      const own = (window.location && window.location.hostname) ? window.location.hostname.replace(/^www\./, '') : '';
      if (!host || host === own) src = 'direct';
      else src = canonSource(host) || host.slice(0, 40);
    }
    localStorage.setItem('r88_src', src);
    return src;
  } catch { return null; }
}

function getDevice() {
  const ua = (navigator.userAgent || '');
  if (/iPad|Tablet|PlayBook|Silk/i.test(ua)) return 'tablet';
  if (/Mobi|Android|iPhone|iPod/i.test(ua)) return 'mobile';
  return 'desktop';
}

// Respect the browser's "Do Not Track" signal — when set we skip the
// third-party IP geolocation lookup entirely (no IP leaves the browser).
function dntEnabled() {
  try {
    const dnt = navigator.doNotTrack || window.doNotTrack || navigator.msDoNotTrack;
    return dnt === '1' || dnt === 'yes';
  } catch { return false; }
}

// ── geo lookup (cached for the whole session) ──────────────────────────────────
// Primary source is our own /api/geo edge function, which reads Vercel's request
// geo headers — free, instant, and with NO third-party rate limit (critical at
// scale). Falls back to two public IP-geo providers if the edge route is
// unavailable (e.g. local dev, or geo headers missing).
async function fetchGeoOnce() {
  try {
    const r = await fetch('/api/geo');
    if (r.ok) {
      const d = await r.json();
      if (d && d.country) return { country: d.country, country_code: d.country_code || null, city: d.city || null, region: d.region || null };
    }
  } catch {}
  try {
    const r = await fetch('https://ipwho.is/?fields=success,country,country_code,city,region');
    const d = await r.json();
    if (d && d.success && d.country) return { country: d.country, country_code: d.country_code || null, city: d.city || null, region: d.region || null };
  } catch {}
  try {
    const r = await fetch('https://get.geojs.io/v1/ip/geo.json');
    const d = await r.json();
    if (d && d.country) return { country: d.country, country_code: d.country_code || null, city: d.city || null, region: d.region || null };
  } catch {}
  return {};
}
let geoPromise = null;
function getGeo() {
  if (dntEnabled()) return Promise.resolve({});
  try {
    const cached = sessionStorage.getItem('r88_geo');
    if (cached) return Promise.resolve(JSON.parse(cached));
  } catch {}
  if (!geoPromise) {
    geoPromise = fetchGeoOnce().then(geo => {
      // Only cache a successful lookup, so a transient failure can retry next load.
      try { if (geo.country) sessionStorage.setItem('r88_geo', JSON.stringify(geo)); } catch {}
      return geo;
    });
  }
  return geoPromise;
}

// ── event tracking ─────────────────────────────────────────────────────────────
// Tries a rich insert first; if the schema hasn't been migrated yet (unknown
// columns), it falls back to the minimal insert so analytics never break.
export async function trackEvent(type, extra = {}) {
  const base = { type, template: extra.template || null, color: extra.color || null };
  try {
    const geo = await getGeo();
    const enriched = {
      ...base,
      session_id: getSessionId(),
      visitor_id: getVisitorId(),
      device: getDevice(),
      referrer: (document.referrer || '').slice(0, 300) || null,
      path: (window.location.pathname || '/').slice(0, 300),
      source: getSource(),
      country: geo.country || null,
      country_code: geo.country_code || null,
      city: geo.city || null,
      user_agent: (navigator.userAgent || '').slice(0, 400) || null,
    };
    const isColErr = (e) => e && /column|schema cache|PGRST204|42703/i.test(`${e.message || ''} ${e.code || ''}`);
    let { error } = await supabase.from('events').insert([enriched]);
    // Degrade gracefully on an unknown-column error (partial migration): first
    // drop the newest optional columns (source, user_agent) so geo/device/session
    // still save, then fall back to the minimal insert only if fully un-migrated.
    if (error && isColErr(error)) {
      const reduced = { ...enriched }; delete reduced.user_agent; delete reduced.source;
      ({ error } = await supabase.from('events').insert([reduced]));
      if (error && isColErr(error)) await supabase.from('events').insert([base]);
    }
  } catch {
    try { await supabase.from('events').insert([base]); } catch {}
  }
}

// ── saved submissions ───────────────────────────────────────────────────────────
// On PDF download we snapshot the resume so the admin can review it and email
// the person back. Stored in the `submissions` table (see scripts/supabase-schema.sql).
export async function saveSubmission(resume, extra = {}) {
  try {
    const p = (resume && resume.personal) || {};
    // Strip the base64 photo before persisting — it's by far the largest field
    // (often 100–500 KB) and isn't needed to review the resume's content.
    const personalLite = { ...p };
    delete personalLite.photo;
    const resumeLite = { ...resume, personal: personalLite };
    const geo = await getGeo();
    await supabase.from('submissions').insert([{
      name: p.name || null,
      email: p.email || null,
      phone: p.phone || null,
      title: p.title || null,
      template: extra.template || null,
      color: extra.color || null,
      resume: resumeLite,           // jsonb — photo-free snapshot for review
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      country: geo.country || null,
      city: geo.city || null,
    }]);
  } catch {
    // silent fail — never break the download flow for analytics
  }
}
